import { useState } from 'react';
import { UserCheck } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { adoptPilotValue } from '@/services/form-differs.service.js';
import { oldNumberChoices, keepOldAsBody } from '@/lib/phone-labels.js';

// R4 (people-create-door T8e): "Use the pilot's" on a pilot_form_differs flag,
// beside its Keep mine button, on the queue page and the deal panel. The server
// writes the form's value to TRACON and Zoho as David's own statement and reads
// it back; the toast names what the record holds now, and every warning the
// answer carries (a flag that could not be cleared, a read-back that failed) is
// its own toast.info. A 409 stale (the record moved since the flag) is the
// server's sentence, then the queue re-derives. On a phone flag the record's
// old number can be kept as another number with a label (AR35); left blank, it
// is replaced, as before. The server keeps it only once TRACON holds people
// (the people switch at both) and says so otherwise. There, over a copy or rule
// main (refs.phone_main_class, Q57) blank keeps it and "replace" sends false.
// Q59 (S22): a phone the client typed, held over a main a rule chose (refs.proposer
// 'client'), reads "Use this number"; the server stamps it as the client's, David
// as actor, never as the pilot's own.

const shown = (group, v) => {
  if (v == null) return null;
  if (group === 'name' && typeof v === 'object') return ['first_name', 'middle_name', 'last_name'].map((k) => v[k]).filter(Boolean).join(' ');
  return String(v);
};

export default function UsePilotValueButton({ item, onDone, compact = false, disabled = false, onBusyChange }) {
  const [busy, setBusy] = useState(false);
  const [keepOldAs, setKeepOldAs] = useState('');
  const id = item.refs?.notification_id;
  const phoneFlag = item.refs?.group === 'phone';
  const byClient = item.refs?.proposer === 'client';
  // A registry-confirmed value is not replaced by David's statement (the server answers 409); Keep mine stays.
  if (!id || item.refs?.registry_confirmed) return null;

  async function adopt() {
    setBusy(true); onBusyChange?.(true);
    try {
      const r = await adoptPilotValue(id, keepOldAsBody(keepOldAs));
      const now = shown(r.group, r.stored);
      toast.success(now ? `On record now: ${now}` : `The pilot's ${r.group} was sent to Zoho`);
      for (const w of r.warnings || []) toast.info(w.message || w.code);
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Could not use the pilot’s value');
    } finally {
      setBusy(false); onBusyChange?.(false);
      onDone?.();
    }
  }

  const button = (
    <button
      type="button"
      title={byClient ? "Put this number on the record (TRACON and Zoho) as the client's" : "Put the pilot form's value on the record (TRACON and Zoho), stated by you"}
      disabled={busy || disabled}
      onClick={adopt}
      className={compact
        ? 'inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-[5px] border border-border px-2 py-1 text-[12px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50'
        : 'inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50'}
    >
      <UserCheck size={compact ? 13 : 14} strokeWidth={1.5} /> {busy ? 'Saving…' : byClient ? 'Use this number' : 'Use the pilot’s'}
    </button>
  );
  if (!phoneFlag) return button;
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      <select
        aria-label="The record's old number"
        title="Keep the record's old number as a 2nd number"
        value={keepOldAs}
        disabled={busy || disabled}
        onChange={(e) => setKeepOldAs(e.target.value)}
        className={`${compact ? 'min-h-[32px] text-[12px]' : 'min-h-[36px] text-[12.5px]'} rounded-[5px] border border-border bg-surface px-1.5 text-text-muted disabled:opacity-50`}
      >
        {oldNumberChoices(item.refs?.phone_main_class).map((o) => <option key={o.value || 'blank'} value={o.value}>{o.label}</option>)}
      </select>
      {button}
    </span>
  );
}
